/**
 * Billing & Analytics — the reports behind the Reports tab.
 *
 * Every report is built once, as a plain table model, and then rendered two
 * ways: as a CSV file and as the preview on the page. Building it once is the
 * point — the preview an admin checks before downloading is the file they get,
 * not a second interpretation of the same data that could drift from it.
 *
 *   report  = { id, title, sections: [section], notes: [string] }
 *   section = { title?, columns: [column], rows: [object], totals?: object }
 *   column  = { key, label, type: 'text' | 'int' | 'decimal' | 'money' | 'percent' | 'date', digits? }
 *
 * The CSV is written for the spreadsheet it will be opened in:
 *
 *   - a UTF-8 byte-order mark, or Excel reads "£" and "–" as mojibake;
 *   - CRLF line endings and RFC 4180 quoting;
 *   - numbers stay numbers (no "£", no thousands separators) so a column can be
 *     summed, with the unit carried in the header instead — "Cost (USD)";
 *   - text that a spreadsheet would run as a formula (=, +, -, @) is defused,
 *     because template and user names are typed by people.
 *
 * Two layouts: "report" adds a title block, totals rows and the notes that say
 * what the figures are; "data" is a single header row and the rows beneath it,
 * for anything that is going to be imported rather than read.
 */
import { allocateByShare, USD_GBP_RATE } from './billing'

// ---------------------------------------------------------------------------
// Encoding
// ---------------------------------------------------------------------------

const BOM = '\uFEFF'
const EOL = '\r\n'

// Spelled out rather than taken from Intl: ICU's en-GB writes "Sept", other
// engines "Sep", and a file should not change with the browser that made it.
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

/**
 * Round half away from zero at `digits` places. Shifting by exponent rather
 * than multiplying keeps 1.005 at 1.01 — `Math.round(1.005 * 100)` is 100,
 * because 1.005 is really 1.00499999… in binary.
 */
const round = (value, digits) => {
  const n = Number(value)
  if (!Number.isFinite(n)) return null
  const shifted = Number(`${Math.abs(n)}e${digits}`)
  const r = Number.isFinite(shifted) && !String(Math.abs(n)).includes('e')
    ? Number(`${Math.round(shifted)}e-${digits}`)
    : Math.round(Math.abs(n) * 10 ** digits) / 10 ** digits
  return n < 0 && r !== 0 ? -r : r
}

/** One CSV cell, quoted only when it has to be and defused when it could run. */
export function csvCell(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : ''
  let s = String(value)
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`
  if (/[",\r\n]/.test(s) || s !== s.trim()) return `"${s.replace(/"/g, '""')}"`
  return s
}

/** The value a column writes to the file: rounded, but still a number. */
export function cellValue(column, value) {
  if (value === null || value === undefined || value === '') return null
  switch (column.type) {
    case 'int': return round(value, 0)
    case 'money': return round(value, column.digits ?? 2)
    case 'percent': return round(value, column.digits ?? 1)
    case 'decimal': return round(value, column.digits ?? 2)
    default: return value
  }
}

/** The same value as the preview shows it, with separators and units. */
export function displayValue(column, value) {
  const v = cellValue(column, value)
  if (v === null) return '—'
  const digits = column.digits ?? (column.type === 'percent' ? 1 : 2)
  switch (column.type) {
    case 'int': return v.toLocaleString('en-GB')
    case 'money':
    case 'decimal':
      return v.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits })
    case 'percent': return `${v.toLocaleString('en-GB', { maximumFractionDigits: digits })}%`
    default: return String(v)
  }
}

const line = cells => cells.map(csvCell).join(',')

/**
 * A plain grid as a spreadsheet-safe CSV: BOM, CRLF, quoting and formula
 * defusing exactly as the reports get them. For the exports that are not a
 * report model (the Cost Explorer ranking, the budget table) but hold the same
 * free-text names.
 *
 * @param rows array of arrays; numbers stay numbers
 */
export function encodeRows(rows) {
  return BOM + (rows || []).map(row => line(row)).join(EOL) + EOL
}

function sectionLines(section, { totals }) {
  const out = [line(section.columns.map(c => c.label))]
  section.rows.forEach(row => out.push(line(section.columns.map(c => cellValue(c, row[c.key])))))
  if (totals && section.totals) {
    out.push(line(section.columns.map(c => cellValue(c, section.totals[c.key]))))
  }
  return out
}

function metaLines(title, context) {
  const out = [line([`Xtract AI · ${title}`])]
  if (context.organisation) out.push(line(['Organisation', context.organisation]))
  if (context.period) out.push(line(['Analysis period', context.period]))
  out.push(line(['Generated', context.generated || formatGenerated()]))
  out.push(line(['Costs', `USD is the Azure consumption estimate; GBP is converted at a fixed ${USD_GBP_RATE} and is indicative only`]))
  return out
}

/**
 * Encode one report.
 *
 * @param layout 'report' (title block, totals, notes, every section) or 'data'
 *   (the first section's header and rows only — nothing a parser would trip on)
 */
export function encodeReport(report, context = {}, layout = 'report') {
  if (layout === 'data') {
    const [first] = report.sections
    return BOM + (first ? sectionLines(first, { totals: false }).join(EOL) : '') + EOL
  }

  const out = metaLines(report.title, context)
  if (report.scope) out.push(line(['Covers', report.scope]))
  report.sections.forEach(section => {
    out.push('')
    if (section.title) out.push(line([section.title]))
    out.push(...sectionLines(section, { totals: true }))
  })
  if (report.notes?.length) {
    out.push('', line(['Notes']))
    report.notes.forEach(note => out.push(line([note])))
  }
  return BOM + out.join(EOL) + EOL
}

/** Every available report in one file, one block per report, for a month-end pack. */
export function encodePack(reports, context = {}) {
  const out = metaLines('Billing report pack', context)
  out.push(line(['Contents', reports.map(r => r.title).join('; ')]))
  reports.forEach(report => {
    out.push('', '', line([report.title.toUpperCase()]))
    if (report.scope) out.push(line(['Covers', report.scope]))
    report.sections.forEach(section => {
      out.push('')
      if (section.title) out.push(line([section.title]))
      out.push(...sectionLines(section, { totals: true }))
    })
    if (report.notes?.length) {
      out.push('')
      report.notes.forEach(note => out.push(line([`Note: ${note}`])))
    }
  })
  return BOM + out.join(EOL) + EOL
}

/** "5 Oct 2026, 14:32 (Europe/London)" — when, and in whose clock. */
export function formatGenerated(now = new Date()) {
  const pad = n => String(n).padStart(2, '0')
  const when = `${now.getDate()} ${MONTHS[now.getMonth()]} ${now.getFullYear()}, ${pad(now.getHours())}:${pad(now.getMinutes())}`
  let zone = ''
  try { zone = Intl.DateTimeFormat().resolvedOptions().timeZone || '' } catch { /* older engines */ }
  return zone ? `${when} (${zone})` : when
}

// ---------------------------------------------------------------------------
// File names
// ---------------------------------------------------------------------------

const PERIOD_SLUGS = {
  week: 'last-7-days',
  month: 'last-30-days',
  '3m': 'last-3-months',
  '6m': 'last-6-months',
  year: 'last-12-months',
  all: 'all-time',
}

export function slugify(text) {
  return String(text || '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/** "xtract-acme-usage-by-template-last-3-months-2026-10-05.csv" */
export function reportFilename(reportId, { organisation, range, custom, now = new Date() } = {}) {
  const period = custom?.from && custom?.to
    ? `${custom.from}-to-${custom.to}`
    : PERIOD_SLUGS[range] || ''
  const parts = ['xtract', slugify(organisation), reportId, period, isoDay(now)]
  return `${parts.filter(Boolean).join('-')}.csv`
}

// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------

function isoDay(d) {
  return d.toISOString().slice(0, 10)
}

/** "2026-10-05" -> "5 Oct 2026". */
export function formatIsoDay(iso) {
  if (!iso) return ''
  const d = new Date(`${iso}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) return iso
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

function weekday(iso) {
  const d = new Date(`${iso}T00:00:00Z`)
  return Number.isNaN(d.getTime()) ? '' : WEEKDAYS[d.getUTCDay()]
}

/**
 * /dashboard/trend labels its days "05 Oct", with no year — unusable in a
 * spreadsheet. The response carries `start`/`end` (ISO dates of its first and
 * last day), which is what to use. For an older response without them the
 * series is known to end on `endIso` (the custom range's last day, or today in
 * UTC, which is what the endpoint counts back from), so the real dates are
 * recoverable from the length alone.
 */
export function trendIsoDates(count, endIso, startIso = null) {
  if (startIso && /^\d{4}-\d{2}-\d{2}/.test(startIso)) {
    const first = new Date(`${String(startIso).slice(0, 10)}T00:00:00Z`)
    if (!Number.isNaN(first.getTime())) {
      return Array.from({ length: count }, (_, i) => isoDay(new Date(first.getTime() + i * 86_400_000)))
    }
  }
  const end = endIso ? new Date(`${String(endIso).slice(0, 10)}T00:00:00Z`) : new Date()
  const base = Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), end.getUTCDate())
  return Array.from({ length: count }, (_, i) => isoDay(new Date(base - (count - 1 - i) * 86_400_000)))
}

/** "2026-09" -> "Sep 2026"; "2026-W13" -> "Week 13, 2026". */
export function formatPeriodKey(key) {
  if (!key) return ''
  const week = /^(\d{4})-W(\d{1,2})$/.exec(key)
  if (week) return `Week ${Number(week[2])}, ${week[1]}`
  const month = /^(\d{4})-(\d{2})$/.exec(key)
  if (month && MONTHS[Number(month[2]) - 1]) return `${MONTHS[Number(month[2]) - 1]} ${month[1]}`
  return key
}

const toGbp = usd => (Number(usd) || 0) * USD_GBP_RATE
const share = (part, whole) => (whole > 0 ? (part / whole) * 100 : null)
const ratio = (a, b) => (b > 0 ? a / b : null)
const sum = (rows, key) => rows.reduce((acc, r) => acc + (Number(r[key]) || 0), 0)

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

/** Volume per period, plus where the current cycle stands against the plan. */
export function buildUsageReport({ usage, plan }) {
  const periods = usage?.monthly_breakdown || []
  const weekly = usage?.grouping === 'weekly'
  const rows = periods.map(p => ({
    period: formatPeriodKey(p.month),
    key: p.month,
    pages: p.pages_processed || 0,
    documents: p.documents_processed || 0,
    requests: p.requests || 0,
    tokens: p.ai_tokens_used || 0,
    pagesPerDoc: ratio(p.pages_processed || 0, p.documents_processed || 0),
  }))
  const totals = {
    period: 'Total',
    pages: sum(rows, 'pages'),
    documents: sum(rows, 'documents'),
    requests: sum(rows, 'requests'),
    tokens: sum(rows, 'tokens'),
  }
  totals.pagesPerDoc = ratio(totals.pages, totals.documents)

  const sections = [{
    title: weekly ? 'Usage by week' : 'Usage by month',
    columns: [
      { key: 'period', label: weekly ? 'Week' : 'Month', type: 'text' },
      { key: 'key', label: 'Period key', type: 'text' },
      { key: 'pages', label: 'Pages processed', type: 'int' },
      { key: 'documents', label: 'Documents processed', type: 'int' },
      { key: 'requests', label: 'Requests', type: 'int' },
      { key: 'tokens', label: 'AI tokens', type: 'int' },
      { key: 'pagesPerDoc', label: 'Pages per document', type: 'decimal' },
    ],
    rows,
    totals,
  }]

  const u = plan?.usage
  if (plan && u) {
    const cycle = plan.cycle || {}
    const kv = [
      ['Plan', plan.name, 'text'],
      ['Billing month starts', cycle.start ? formatIsoDay(String(cycle.start).slice(0, 10)) : null, 'text'],
      ['Days elapsed', cycle.days_elapsed, 'int'],
      ['Days in billing month', cycle.days_total, 'int'],
      ['Included pages', u.pages_included, 'int'],
      ['Pages used so far', u.pages_used, 'int'],
      ['Allowance used (%)', u.percent_used, 'percent'],
      ['Pages remaining', u.pages_remaining, 'int'],
      ['Projected pages at end of billing month', u.projected_pages, 'int'],
      ['Projected allowance used (%)', u.projected_percent, 'percent'],
      ['Projected overage pages', u.projected_overage_pages, 'int'],
      ['Overage rate per page (GBP)', plan.overage_per_page, 'money'],
      ['Base monthly fee (GBP)', plan.monthly_fee, 'money'],
      ['Projected overage cost (GBP)', u.projected_overage_cost, 'money'],
      ['Projected billing month total (GBP)', u.projected_total_cost, 'money'],
    ]
    // Rows carry their own type, so a key/value table still writes numbers.
    sections.push({
      title: 'Plan position, current billing month',
      columns: [{ key: 'metric', label: 'Metric', type: 'text' }, { key: 'value', label: 'Value', type: 'cell' }],
      rows: kv.filter(([, v]) => v !== undefined && v !== null && v !== '')
        .map(([metric, value, type]) => ({ metric, value: cellValue({ type }, value) })),
    })
  }

  return {
    id: 'usage',
    title: 'Usage and plan position',
    scope: rows.length ? `${rows[0].period} to ${rows[rows.length - 1].period}` : null,
    sections,
    notes: [
      'Usage periods follow the analysis period. The plan position is always the current billing month.',
      'Plan figures are in GBP, the currency your plan is billed in.',
    ],
    rowCount: rows.length,
    empty: rows.length === 0,
  }
}

/**
 * Requests per template, from /dashboard/trend. Counts are measured; the cost
 * beside them is the current cycle's Azure estimate allocated by share of
 * requests — exactly as the Templates tab presents it.
 */
export function buildTemplateReport({ trend, consumption = 0, endIso, variant = 'summary' }) {
  const dates = trend?.dates || []
  // The response's own first/last day when it has them; else today (or the
  // custom range's end) counted back from the series length.
  const iso = trendIsoDates(dates.length, trend?.end || endIso, trend?.start)
  const active = (trend?.series || []).filter(s => (s.total || 0) > 0)
  const allocated = allocateByShare(active, Number(consumption) || 0)
    .sort((a, b) => (b.total || 0) - (a.total || 0))
  const totalRequests = sum(allocated, 'total')
  const name = s => s.template_name || (s.template_id != null ? `Template #${s.template_id}` : 'No template (dynamic extraction)')

  const scope = iso.length ? `${formatIsoDay(iso[0])} to ${formatIsoDay(iso[iso.length - 1])} (${iso.length} days)` : null
  const notes = [
    'Request counts are measured per day from the requests raised against each template.',
    `Allocated cost is not measured: it is the current billing month's Azure estimate ($${(round(consumption, 2) ?? 0).toFixed(2)}) split by each template's share of requests. Read it as where spend concentrates, not as a bill.`,
  ]

  if (variant === 'daily') {
    // Template names are free text, so two can match; the id keeps the columns apart.
    const seen = new Map()
    allocated.forEach(s => seen.set(name(s), (seen.get(name(s)) || 0) + 1))
    const columns = [
      { key: 'date', label: 'Date', type: 'date' },
      { key: 'weekday', label: 'Day', type: 'text' },
      ...allocated.map((s, i) => ({
        key: `t${i}`,
        label: seen.get(name(s)) > 1 && s.template_id != null ? `${name(s)} (#${s.template_id})` : name(s),
        type: 'int',
      })),
      { key: 'total', label: 'Total requests', type: 'int' },
    ]
    const rows = iso.map((date, d) => {
      const row = { date, weekday: weekday(date), total: 0 }
      allocated.forEach((s, i) => {
        const n = s.counts?.[d] || 0
        row[`t${i}`] = n
        row.total += n
      })
      return row
    })
    const totals = { date: 'Total', total: totalRequests }
    allocated.forEach((s, i) => { totals[`t${i}`] = s.total || 0 })
    return {
      id: 'templates-daily',
      title: 'Daily activity by template',
      scope,
      sections: [{ title: 'Requests per template per day', columns, rows, totals }],
      notes: [notes[0]],
      rowCount: rows.length,
      empty: allocated.length === 0,
    }
  }

  const rows = allocated.map((s, i) => {
    const counts = s.counts || []
    let peak = -1
    let first = -1
    let last = -1
    counts.forEach((n, d) => {
      if (n > 0) {
        if (first === -1) first = d
        last = d
        if (peak === -1 || n > counts[peak]) peak = d
      }
    })
    const activeDays = counts.filter(n => n > 0).length
    return {
      rank: i + 1,
      template: name(s),
      id: s.template_id ?? null,
      requests: s.total || 0,
      share: share(s.total || 0, totalRequests),
      activeDays,
      perActiveDay: ratio(s.total || 0, activeDays),
      peakDay: peak >= 0 ? iso[peak] : null,
      peakRequests: peak >= 0 ? counts[peak] : null,
      first: first >= 0 ? iso[first] : null,
      last: last >= 0 ? iso[last] : null,
      costUsd: s.allocated,
      costGbp: toGbp(s.allocated),
    }
  })

  return {
    id: 'templates',
    title: 'Usage by template',
    scope,
    sections: [{
      columns: [
        { key: 'rank', label: 'Rank', type: 'int' },
        { key: 'template', label: 'Template', type: 'text' },
        { key: 'id', label: 'Template ID', type: 'int' },
        { key: 'requests', label: 'Requests', type: 'int' },
        { key: 'share', label: 'Share of requests (%)', type: 'percent' },
        { key: 'activeDays', label: 'Active days', type: 'int' },
        { key: 'perActiveDay', label: 'Requests per active day', type: 'decimal' },
        { key: 'peakDay', label: 'Busiest day', type: 'date' },
        { key: 'peakRequests', label: 'Busiest day requests', type: 'int' },
        { key: 'first', label: 'First request', type: 'date' },
        { key: 'last', label: 'Latest request', type: 'date' },
        { key: 'costUsd', label: 'Allocated cost (USD)', type: 'money' },
        { key: 'costGbp', label: 'Allocated cost (GBP)', type: 'money' },
      ],
      rows,
      totals: {
        template: 'Total',
        requests: totalRequests,
        share: totalRequests > 0 ? 100 : null,
        costUsd: sum(rows, 'costUsd'),
        costGbp: sum(rows, 'costGbp'),
      },
    }],
    notes,
    rowCount: rows.length,
    empty: rows.length === 0,
  }
}

const SERVICE_LABELS = {
  normalisation: 'Field normalisation',
  email: 'Email body reading',
  llm_fallback: 'LLM fallback extraction',
  ai_learning: 'AI learning',
}

/** Every line behind the Azure consumption estimate, plus the fixed platform cost. */
export function buildConsumptionReport({ costs, infrastructure, scope = 'period' }) {
  const data = costs?.[scope] || {}
  const pricing = costs?.pricing || {}
  const total = Number(data.total_estimated_usd) || 0
  const lineRow = (item, category, quantity, unit, rate, usd) => ({
    item, category, quantity, unit, rate, usd, gbp: toGbp(usd), share: share(Number(usd) || 0, total),
  })

  const money = [
    { key: 'item', label: 'Line item', type: 'text' },
    { key: 'category', label: 'Category', type: 'text' },
    { key: 'quantity', label: 'Quantity', type: 'int' },
    { key: 'unit', label: 'Unit', type: 'text' },
    { key: 'rate', label: 'Rate (USD)', type: 'money', digits: 4 },
    { key: 'usd', label: 'Cost (USD)', type: 'money', digits: 4 },
    { key: 'gbp', label: 'Cost (GBP)', type: 'money', digits: 4 },
    { key: 'share', label: 'Share of total (%)', type: 'percent' },
  ]

  // Document processing is the per-page fee plus the tokens of the model
  // Content Understanding runs. Where the server splits the two, they are two
  // lines, so quantity x rate reproduces each cost and the lines still add up
  // to the total; an older payload has one combined figure and one line.
  const split = data.document_processing_page_usd !== undefined && data.document_processing_tokens_usd !== undefined
  const rows = [
    lineRow('Document processing', 'Azure Content Understanding', data.pages_billed || 0, 'pages',
      pricing.per_page_usd, split ? data.document_processing_page_usd : (data.document_processing_usd || 0)),
  ]
  if (split && Number(data.document_processing_tokens_usd) > 0) {
    rows.push(lineRow('Document processing, model tokens', 'Azure Content Understanding',
      (data.cu_input_tokens || 0) + (data.cu_output_tokens || 0), 'tokens (rate per 1M, input and output differ)',
      null, data.document_processing_tokens_usd))
  }
  rows.push(
    lineRow('AI inference, input', 'Azure OpenAI', data.input_tokens || 0, 'tokens (rate per 1M)',
      pricing.per_1m_input_tokens_usd, data.ai_input_cost_usd || 0),
    lineRow('AI inference, output', 'Azure OpenAI', data.output_tokens || 0, 'tokens (rate per 1M)',
      pricing.per_1m_output_tokens_usd, data.ai_output_cost_usd || 0),
  )

  const sections = [{
    title: 'Azure consumption estimate',
    columns: money,
    rows,
    totals: { item: 'Total', usd: total, gbp: toGbp(total), share: total > 0 ? 100 : null },
  }]

  const services = Object.entries(data.services || {})
  if (services.length) {
    const svcRows = services.map(([key, svc]) => ({
      service: SERVICE_LABELS[key] || key,
      input: svc.input_tokens || 0,
      output: svc.output_tokens || 0,
      tokens: svc.total_tokens || 0,
      usd: svc.estimated_cost_usd || 0,
      gbp: toGbp(svc.estimated_cost_usd || 0),
    }))
    sections.push({
      title: 'AI inference by service (a breakdown of the AI lines above, not in addition to them)',
      columns: [
        { key: 'service', label: 'Service', type: 'text' },
        { key: 'input', label: 'Input tokens', type: 'int' },
        { key: 'output', label: 'Output tokens', type: 'int' },
        { key: 'tokens', label: 'Total tokens', type: 'int' },
        { key: 'usd', label: 'Cost (USD)', type: 'money', digits: 4 },
        { key: 'gbp', label: 'Cost (GBP)', type: 'money', digits: 4 },
      ],
      rows: svcRows,
      totals: {
        service: 'Total',
        input: sum(svcRows, 'input'),
        output: sum(svcRows, 'output'),
        tokens: sum(svcRows, 'tokens'),
        usd: sum(svcRows, 'usd'),
        gbp: sum(svcRows, 'gbp'),
      },
    })
  }

  const infra = Object.values(infrastructure?.services || {})
  if (infra.length) {
    const infraRows = infra.map(s => ({
      service: s.name, category: s.category, description: s.description,
      usd: s.monthly_usd || 0, gbp: toGbp(s.monthly_usd || 0),
    }))
    sections.push({
      title: 'Platform infrastructure (fixed monthly, not part of the consumption total)',
      columns: [
        { key: 'service', label: 'Resource', type: 'text' },
        { key: 'category', label: 'Category', type: 'text' },
        { key: 'description', label: 'Description', type: 'text' },
        { key: 'usd', label: 'Monthly cost (USD)', type: 'money' },
        { key: 'gbp', label: 'Monthly cost (GBP)', type: 'money' },
      ],
      rows: infraRows,
      totals: { service: 'Total', usd: sum(infraRows, 'usd'), gbp: sum(infraRows, 'gbp') },
    })
  }

  return {
    id: 'azure-consumption',
    title: 'Azure consumption breakdown',
    scope: null,
    sections,
    notes: [
      costs?.pricing_notes?.disclaimer || 'Estimates based on published Azure pricing. Actual costs may vary.',
      'These are estimates, not an invoice. Your Azure Marketplace billing is the authority for what you are charged.',
    ],
    rowCount: rows.length,
    empty: !costs?.[scope],
  }
}

/**
 * Volume per person, and per group, attributed to whoever raised the request.
 *
 * `totals` is everyone in the window ({ requests, documents, billable_pages },
 * summary.user_activity_totals). The rows are only the top of the table, so a
 * total or a share measured against their sum would overstate every one of
 * them; without `totals` (an older backend) the rows' own sum is all there is.
 * With `totals`, an "Everyone else" row carries the difference, so the rows
 * add up to the total row.
 */
export function buildPeopleReport({ activity, groupActivity, totals = null, scope = 'period' }) {
  const people = [...(activity?.[scope] || [])].sort((a, b) => (b.requests || 0) - (a.requests || 0))
  const hasTotals = totals && Number.isFinite(Number(totals.requests))
  const totalRequests = hasTotals ? Number(totals.requests) : sum(people, 'requests')
  const totalDocuments = hasTotals && Number.isFinite(Number(totals.documents)) ? Number(totals.documents) : sum(people, 'documents')
  // Pages each person was charged for. Absent on an older backend, null when
  // the ledger could not be read: neither is ever written as a zero.
  const hasBillable = people.some(u => u.billable_pages !== undefined)
  const billableKnown = people.every(u => u.billable_pages !== null)
  const rows = people.map((u, i) => ({
    rank: i + 1,
    user: u.username || u.email || `User #${u.id}`,
    email: u.email || null,
    requests: u.requests || 0,
    share: share(u.requests || 0, totalRequests),
    documents: u.documents || 0,
    docsPerRequest: ratio(u.documents || 0, u.requests || 0),
    billable: u.billable_pages === undefined ? null : u.billable_pages,
  }))

  const columns = [
    { key: 'rank', label: 'Rank', type: 'int' },
    { key: 'user', label: 'User', type: 'text' },
    { key: 'email', label: 'Email', type: 'text' },
    { key: 'requests', label: 'Requests', type: 'int' },
    { key: 'share', label: 'Share of requests (%)', type: 'percent' },
    { key: 'documents', label: 'Documents', type: 'int' },
    { key: 'docsPerRequest', label: 'Documents per request', type: 'decimal' },
  ]
  if (hasBillable) columns.push({ key: 'billable', label: 'Billable pages', type: 'int' })

  const sectionTotals = {
    user: 'Total',
    requests: totalRequests,
    share: totalRequests > 0 ? 100 : null,
    documents: totalDocuments,
    docsPerRequest: ratio(totalDocuments, totalRequests),
  }
  if (hasBillable && billableKnown) {
    // Number(null) is 0: an organisation total the ledger couldn't give is left
    // blank, never written as 0 billable pages.
    const orgTotal = totals?.billable_pages
    sectionTotals.billable = !hasTotals
      ? sum(rows, 'billable')
      : orgTotal !== null && orgTotal !== undefined && Number.isFinite(Number(orgTotal)) ? Number(orgTotal) : null
  }

  // Everyone below the listed people, so the rows add up to the total row.
  if (hasTotals) {
    const known = v => v !== null && v !== undefined && Number.isFinite(Number(v))
    const restRequests = Math.max(0, totalRequests - sum(people, 'requests'))
    const restDocuments = Math.max(0, totalDocuments - sum(people, 'documents'))
    const restBillable = hasBillable && billableKnown && known(totals.billable_pages)
      ? Math.max(0, Number(totals.billable_pages) - sum(people, 'billable_pages'))
      : null
    if (restRequests > 0 || restDocuments > 0 || restBillable > 0) {
      rows.push({
        rank: null,
        user: 'Everyone else',
        email: null,
        requests: restRequests,
        share: share(restRequests, totalRequests),
        documents: restDocuments,
        docsPerRequest: ratio(restDocuments, restRequests),
        billable: restBillable,
      })
    }
  }

  const sections = [{ title: 'Usage by person', columns, rows, totals: sectionTotals }]

  const groups = [...(groupActivity?.[scope] || [])].sort((a, b) => (b.requests || 0) - (a.requests || 0))
  if (groups.length) {
    sections.push({
      title: 'Usage by group (members can belong to several groups, so these do not add up to the people total)',
      columns: [
        { key: 'group', label: 'Group', type: 'text' },
        { key: 'members', label: 'Members', type: 'int' },
        { key: 'requests', label: 'Requests', type: 'int' },
        { key: 'documents', label: 'Documents', type: 'int' },
        { key: 'perMember', label: 'Requests per member', type: 'decimal' },
      ],
      rows: groups.map(g => ({
        group: g.name,
        members: g.member_count || 0,
        requests: g.requests || 0,
        documents: g.documents || 0,
        perMember: ratio(g.requests || 0, g.member_count || 0),
      })),
    })
  }

  return {
    id: 'usage-by-person',
    title: 'Usage by person',
    scope: null,
    sections,
    notes: [
      hasTotals
        ? 'Rows are the people with the most requests, then "Everyone else" for the rest; the total row and every share cover everyone in the period. Volume is attributed to whoever raised the request. Service accounts, such as the Outlook add-in, appear as their own row.'
        : 'Volume is attributed to whoever raised the request. Service accounts, such as the Outlook add-in, appear as their own row.',
    ],
    rowCount: rows.length,
    empty: rows.length === 0,
  }
}

const statusLabel = s => String(s || 'unknown')
  .replace(/[_-]+/g, ' ')
  .replace(/\b\w/g, c => c.toUpperCase())

/** Request counts by status, every month on record — from /dashboard/export-report?format=json. */
export function buildMonthlyReport(payload) {
  const statuses = payload?.statuses || []
  const months = payload?.months || []
  const rows = months.map(m => {
    const row = { month: formatPeriodKey(m.month), key: m.month, total: m.total || 0 }
    statuses.forEach((s, i) => { row[`s${i}`] = m[s] || 0 })
    return row
  })
  const totals = { month: 'Total', total: sum(rows, 'total') }
  statuses.forEach((_, i) => { totals[`s${i}`] = sum(rows, `s${i}`) })

  return {
    id: 'monthly-requests',
    title: 'Monthly request report',
    scope: rows.length ? `${rows[0].month} to ${rows[rows.length - 1].month}` : null,
    sections: [{
      title: 'Requests by month and status',
      columns: [
        { key: 'month', label: 'Month', type: 'text' },
        { key: 'key', label: 'Period key', type: 'text' },
        ...statuses.map((s, i) => ({ key: `s${i}`, label: statusLabel(s), type: 'int' })),
        { key: 'total', label: 'Total requests', type: 'int' },
      ],
      rows,
      totals,
    }],
    notes: ['Every month on record, independent of the analysis period. Each request is counted under its status today.'],
    rowCount: rows.length,
    empty: rows.length === 0,
  }
}
